import { GridBaseDataProvider } from '../../grid/GridBaseDataProvider';
import { GetRawEventsResponse, GetSummaryRequest } from '@nirvana/shared/dtos/types/raw-events/gz-console';
import {
  SetFiltersRequest,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
} from '@nirvana/shared/dtos/types/alerts/gz-console';
import { Column, CompanySpecialValues, Filter, FiltersHelper } from '@nirvana/shared/dtos/types/shared/filters';
import { getRequestDefaultOptions } from '../../utils/request.utils';
import { sanitizeObject } from '../../utils/sanitization.utils';
import { convertRelativeDateFiltersToAbsolute } from '../../grid/utils/filters';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { GetRowsRequest, GetSummaryResponse } from '../../grid/types/common';
import { ColumnDetails, ColumnKeys } from '@nirvana/shared/dtos/types/alerts/common';
import { API_TYPE_ALERTS } from '../../webcomponents.constants';

export class AlertsGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  public firstPageAccessedTimestamp: number;
  public companyFilterDataProvider = API_TYPE_ALERTS;

  private currentlyGettingAllDirectManagement: Promise<string[]> | undefined;

  constructor(
    public dependencies: any,
    private readonly allColumnDetails: Array<ColumnDetails>,
    private readonly defaultTargetIds: Array<string>,
    public isPartner: boolean,
    public allDirectManagement?: Array<string>
  ) {
    super();
  }

  async getSummary(request: GetSummaryRequest): Promise<GetSummaryResponse> {
    const dateNow = new Date();
    // we need to use the timestamp from 2 seconds ago
    this.firstPageAccessedTimestamp = new Date(dateNow.getTime() - 2 * 1000).getTime();

    await this.handleAllDirectManagement(request.filters);
    const requestOptions = getRequestDefaultOptions();
    requestOptions.requestTimeout = 10 * 60000;

    const response = await this.dependencies.api.clients.RawEvents.getSummary(
      {
        filters: this.getFiltersForBackend(request.filters, this.firstPageAccessedTimestamp),
      },
      requestOptions
    ).toPromise();

    const itemsCounterText = this.dependencies.translateFn('{numItems} items', {
      numItems: response.exceedsMaxCountLimit ? `${response.numItems}+` : response.numItems,
    });
    const occurrencesCounterText = this.dependencies.translateFn('(number of occurrences: {numOccurrences})', {
      numOccurrences: response.exceedsMaxCountLimit ? `${response.numOccurrences}+` : response.numOccurrences,
    });

    return {
      summary: `${itemsCounterText} ${occurrencesCounterText}`,
      numItems: response.numItems,
    };
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetRawEventsResponse> {
    await this.handleAllDirectManagement(request.filters);
    const requestOptions = getRequestDefaultOptions();
    requestOptions.requestTimeout = 10 * 60000;

    const response = await this.dependencies.api.clients.RawEvents.getRawEvents(
      {
        columns: request.columns,
        filters: this.getFiltersForBackend(request.filters, this.firstPageAccessedTimestamp),
        numRecordsToSkip: request.numRecordsToSkip,
        numRecordsToFetch: request.numRecordsToFetch,
      },
      requestOptions
    ).toPromise();
    return sanitizeObject(response);
  }

  async updateSettings(request: UpdateSettingsRequest): Promise<UpdateSettingsResponse> {
    const response = await this.dependencies.api.clients.Alerts.updateSettings(
      request,
      getRequestDefaultOptions()
    ).toPromise();

    return sanitizeObject(response);
  }

  async setFilters(request: SetFiltersRequest): Promise<{} | undefined> {
    const response = await this.dependencies.api.clients.Alerts.setFilters(
      request,
      getRequestDefaultOptions()
    ).toPromise();

    return sanitizeObject(response);
  }

  public getFiltersForBackend(filters: Array<Filter>, insertedBeforeTimestamp: number): Array<Filter> {
    const insertedAtFilter = this.createInsertedAtFilter(insertedBeforeTimestamp);
    const companyFilter = this.handleCompanyFilter(filters);
    const timezone = this.dependencies.getTimezone();
    const filtersWithConvertedDates = companyFilter.map(filter =>
      convertRelativeDateFiltersToAbsolute<ColumnKeys, Column>(filter, this.allColumnDetails, timezone)
    ) as Array<Filter>;

    return filtersWithConvertedDates.concat(insertedAtFilter);
  }

  private async handleAllDirectManagement(filters: Array<Filter>) {
    if (!this.needsAllDirectManagement(filters)) {
      return;
    }

    if (this.currentlyGettingAllDirectManagement) {
      await this.currentlyGettingAllDirectManagement;
      return;
    }

    this.currentlyGettingAllDirectManagement = this.getAllDirectManagement();

    this.allDirectManagement = await this.currentlyGettingAllDirectManagement;
  }

  private needsAllDirectManagement(filters: Array<Filter>): boolean {
    if (!this.isPartner) {
      return false;
    }

    const companyFilter = FiltersHelper.getCompanyIdsFromView(filters);
    return companyFilter.includes(CompanySpecialValues.ALL_DIRECT_MANAGEMENT) && !this.allDirectManagement;
  }

  private async getAllDirectManagement(): Promise<Array<string>> {
    const { allDirectManagement } = await this.dependencies.api.clients.Alerts.getAllDirectManagement(
      {},
      getRequestDefaultOptions()
    ).toPromise();

    return allDirectManagement;
  }

  private createInsertedAtFilter(insertedBeforeTimestamp: number): Filter {
    return {
      insertedAt: {
        timeRange: {
          kind: TimeRangeKind.ABSOLUTE_TIME_RANGE,
          absolute: {
            toTimestamp: insertedBeforeTimestamp,
          },
        },
      },
    } as Filter;
  }

  private handleCompanyFilter(filters: Array<Filter>): Array<Filter> {
    if (!this.isPartner) {
      const companyFilter = {
        company: {
          in: this.defaultTargetIds,
        },
      };

      return filters.concat(companyFilter);
    }

    const existingCompanyFilter = filters.find(filter => filter.company);

    if (!existingCompanyFilter) {
      throw new Error('Unable to get company filter!');
    }

    if (existingCompanyFilter.company?.in?.includes(CompanySpecialValues.ALL_DIRECT_MANAGEMENT)) {
      existingCompanyFilter.company.in = this.allDirectManagement;
    }

    return filters;
  }
}
